import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, Lock, Broom } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ROLE_LABELS } from "@/contexts/AuthContext";

const ROLES = ["super_admin", "admin_ops", "sales_ib", "broker_viewer", "participant_viewer"];

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "admin_ops" });

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/users"); setUsers(data); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/users", form);
      toast.success("User created");
      setCreating(false);
      setForm({ name: "", email: "", password: "", role: "admin_ops" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateRole = async (id, role) => {
    try { await api.put(`/users/${id}`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleDisable = async (u) => {
    try { await api.put(`/users/${u.id}`, { disabled: !u.disabled }); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const resetDemoData = async () => {
    const confirm1 = window.confirm(
      "This will PERMANENTLY DELETE all participants, trades, milestones, rewards, CRM notes, and campaign audit logs.\n\nUser accounts and broker integrations will be kept.\n\nUse this ONLY before switching to real broker data. Continue?"
    );
    if (!confirm1) return;
    const typed = window.prompt('Type "RESET" (uppercase) to confirm:');
    if (typed !== "RESET") { toast.error("Reset cancelled"); return; }
    try {
      const { data } = await api.post("/admin/reset-demo-data");
      const total = Object.values(data.wiped).reduce((a, b) => a + b, 0);
      toast.success(`Demo data wiped · ${total} records deleted`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto">
      <PageHeader
        title="User Management"
        subtitle="Manage operations team accounts and role-based access."
        breadcrumbs="Admin / Users"
        testId="users-page"
        actions={
          <div className="flex gap-2">
            <button data-testid="reset-demo-btn" onClick={resetDemoData} className="inline-flex items-center gap-2 px-4 py-2 border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-300 text-sm rounded-md">
              <Broom size={14} /> Reset Demo Data
            </button>
            <button data-testid="user-create-btn" onClick={() => setCreating(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md">
              <Plus size={14} /> New User
            </button>
          </div>
        }
      />

      <div className="bg-[#111827] border border-white/10 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-[#1F2937]/50 text-left text-[10px] uppercase tracking-widest text-gray-500">
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={5} className="p-8 text-center text-gray-500">Loading…</td></tr> :
                users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-white/5">
                    <td className="px-4 py-3">
                      <p className="text-white">{u.name}</p>
                      <p className="text-xs text-gray-500 font-mono">{u.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <select data-testid={`user-role-${u.id}`} value={u.role} onChange={(e) => updateRole(u.id, e.target.value)} className="bg-[#0B0F19] border border-white/10 rounded-md px-2 py-1 text-xs text-white outline-none">
                        {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-400">{(u.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${u.disabled ? "bg-red-500/10 text-red-300 border-red-500/30" : "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"}`}>
                        {u.disabled ? "Disabled" : "Active"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button data-testid={`user-toggle-${u.id}`} onClick={() => toggleDisable(u)} className="p-1.5 rounded-md hover:bg-white/5 text-gray-400 hover:text-orange-400" title={u.disabled ? "Enable" : "Disable"}>
                          <Lock size={14} />
                        </button>
                        <button data-testid={`user-delete-${u.id}`} onClick={() => remove(u)} className="p-1.5 rounded-md hover:bg-red-500/10 text-red-400" title="Delete">
                          <Trash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-[#111827] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-display">Create new user</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <input data-testid="user-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-[#0B0F19] border border-white/10 rounded-md p-2.5 text-sm outline-none" />
            <input data-testid="user-email" type="email" placeholder="email@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-[#0B0F19] border border-white/10 rounded-md p-2.5 text-sm outline-none" />
            <input data-testid="user-password" type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full bg-[#0B0F19] border border-white/10 rounded-md p-2.5 text-sm outline-none" />
            <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full bg-[#0B0F19] border border-white/10 rounded-md p-2.5 text-sm outline-none">
              {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
          </div>
          <DialogFooter>
            <button onClick={() => setCreating(false)} className="px-4 py-2 text-sm text-gray-400">Cancel</button>
            <button data-testid="user-submit" onClick={create} className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md">Create</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
